import { ColumnPinnedType } from 'ag-grid-community';
import {
  BrokerNerdsBarkerCoreEnumsFulfillmentStatus,
  BrokerNerdsBarkerCoreEnumsMarketplace,
  BrokerNerdsBarkerCoreEnumsPaymentStatus,
  BrokerNerdsBarkerCoreEnumsPricerStatus,
  BrokerNerdsBarkerCoreModelsInventoryAddListingRequest,
  BrokerNerdsBarkerCoreModelsInventoryEvent,
  BrokerNerdsBarkerCoreModelsInventoryListing,
  BrokerNerdsBarkerCoreModelsInventoryPosNextSplitRules,
  BrokerNerdsBarkerCoreModelsInventorySeasonLocation,
  BrokerNerdsBarkerCoreModelsMarketplacesEvent,
  BrokerNerdsBarkerCoreModelsMarketplacesListing,
  BrokerNerdsBarkerCoreModelsMarketplacesSeatingChartZone,
  BrokerNerdsBarkerCoreModelsPricingRule,
  BrokerNerdsBarkerCoreModelsPricingRuleBase,
  BrokerNerdsBarkerCoreModelsSalesSaleBase,
  GetApiSalesParams,
} from './api';
import { ExtendedVendorProperties } from './types-vendorProperties';
import { DeepOmit, StringWithPipe, UnDot, WithRequiredProperty } from './ts-utils';
import { PRICE_VARIATIONS } from './utils/globals';

export type BrokerNerdsBarkerCoreModelsInventoryGetInventoryResponseExtended = {
  events: BrokerNerdsBarkerCoreModelsInventoryEvent[];
  listings: BrokerNerdsBarkerCoreModelsInventoryListingExtended[];
};

export type BrokerNerdsBarkerCoreModelsInventoryListingExtended = BrokerNerdsBarkerCoreModelsInventoryListing & UIListingProps;

export type UIListingProps = {
  ruleCount?: number;
};

export type BarkerSelectedEventListing = {
  event: BrokerNerdsBarkerCoreModelsInventoryEvent;
  listing: BrokerNerdsBarkerCoreModelsInventoryListing & UIListingProps;
};

export type BarkerEventListing = {
  event: BrokerNerdsBarkerCoreModelsInventoryEvent;
  sale?: BrokerNerdsBarkerCoreModelsSalesSaleBase;
  meta?: Record<string, unknown>;
  tenantIdEventId: StringWithPipe;
  tenantIdListingId: StringWithPipe;
} & UIListingProps &
  WithRequiredProperty<BrokerNerdsBarkerCoreModelsInventoryListing, 'listingId'>;

export type BarkerEventListingWithPending = { pendingBroadcast?: boolean | null | undefined } & BarkerEventListing;

export type SeatingChartInstance = {
  rows: string;
  splits: string;
  exclusions: string;
  price: string;
  marketplaceEventId: string;
  selectedMarketplace: BrokerNerdsBarkerCoreEnumsMarketplace;
};

export type GetSeatingChartResponse = {
  asOfDate: Date;
  svgUrl: string;
  zones: BrokerNerdsBarkerCoreModelsMarketplacesSeatingChartZone[] | undefined;
};

// export type FiltersWithNullableOutlierCriteria = Omit<BrokerNerdsBarkerCoreModelsPricingRuleBase['filters'], 'outlierCriteria'> & { outlierCriteria?: BrokerNerdsBarkerCoreModelsPricingOutlierCriteria | null };

// export type Rule = Omit<
//   BrokerNerdsBarkerCoreModelsPricingRuleBase & { ruleId: string | null; meta?: { adjustmentOverride?: boolean; numComparablesOverride?: boolean } },
//   'pointOfSaleEventId' | 'tenantId' | 'filters'
// > &
//   { filters: FiltersWithNullableOutlierCriteria } &
//   Partial<Pick<BrokerNerdsBarkerCoreModelsPricingRule, 'updatedAt'>>;

export type Rule = Omit<
  BrokerNerdsBarkerCoreModelsPricingRuleBase & { ruleId: string | null; meta?: { isLocalRule?: boolean; adjustmentOverride?: boolean; numComparablesOverride?: boolean } },
  'pointOfSaleEventId' | 'tenantId'
> &
  Partial<Pick<BrokerNerdsBarkerCoreModelsPricingRule, 'updatedAt'>>;

export type ListingUpdate = keyof BrokerNerdsBarkerCoreModelsInventoryListing extends infer K extends keyof BrokerNerdsBarkerCoreModelsInventoryListing
  ? K extends K
    ? {
        listingId: string;
        tenantId: string;
        property: K;
        value: BrokerNerdsBarkerCoreModelsInventoryListing[K];
        previousValue: BrokerNerdsBarkerCoreModelsInventoryListing[K];
        inlinePriceChange?: boolean;
      }
    : never
  : never;

export type GlobalTransientState = {
  autoUpdateAutoPricedListPriceRuleId: string | null | undefined;
  applyGroupListingRule: boolean;
  ignoreGridScrolling: boolean;
  expandGroupByListingId: StringWithPipe | null;
};

export type PriceVariation = (typeof PRICE_VARIATIONS)[number];

export type MarketplacesListingWithAlias = {
  sectionAlias?: string;
} & BrokerNerdsBarkerCoreModelsMarketplacesListing;

export type PartialBrokerNerdsBarkerCoreModelsInventoryPosNextSplitRules = DeepOmit<BrokerNerdsBarkerCoreModelsInventoryPosNextSplitRules, UnDot<'splitRule.name'>>;

export type SectionCounts = { sectionId: string; section: string; count: number; totalQuantity: number };
export type RecordSectionCounts = Record<string, SectionCounts>;

export type SeasonSummary = Omit<BrokerNerdsBarkerCoreModelsInventorySeasonLocation, 'listings' | 'row' | 'section'> & {
  numLocations: number;
  numEvents: number;
  seasonYear: string;
  locations: InventorySeasonLocationExtended[];
};

export type InventorySeasonLocationExtended = BrokerNerdsBarkerCoreModelsInventorySeasonLocation & {
  hashId: string;
  ruleId?: string;
  ruleTier?: number;
  ruleCount?: number;
};

export type MarketplaceEventExtended = {
  marketplaceEventId: string | null | undefined;
  svgDetails: any;
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace | null | undefined;
  marketplaceEvent: BrokerNerdsBarkerCoreModelsMarketplacesEvent | null | undefined;
  isUserMapped: boolean;
  metadata?: string | null;
  sections: BrokerNerdsBarkerCoreModelsMarketplacesSeatingChartZone[];
  isLoading: boolean;
};

export interface Window {
  store: any;
}

export type InventorySearchParams = {
  query: string;
  fromDate: string | null | undefined;
  toDate: string | null | undefined;
  pricerStatuses: BrokerNerdsBarkerCoreEnumsPricerStatus[] | undefined;
  purchaseDateFrom: string | undefined;
  purchaseDateTo: string | undefined;
  daysSinceRepriced: number | undefined;
  daysSinceViewed: number | undefined;
  listingId: string | undefined;
  eventId: string | undefined;
  tags: string[] | undefined;
  antiTags: string[] | undefined;
  daysSinceLastSale: number | undefined;
  daysSinceFirstSeen: number | undefined;
  daysSinceRuleUpdated: number | undefined;
  onlyNotBroadcasting: boolean | undefined;
  showEvents: 'open' | 'mine' | 'all' | undefined;
};

export type InventorySearchParamsWithDates = {
  fromDate: Date | null | undefined;
  toDate: Date | null | undefined;
  purchaseDateFrom: Date | undefined;
  purchaseDateTo: Date | undefined;
  tags: string[];
  antiTags: string[];
} & Omit<InventorySearchParams, 'fromDate' | 'toDate' | 'purchaseDateFrom' | 'purchaseDateTo'>;

export type SalesSearchParamsWithDates = {
  fromDate: Date | null | undefined;
  toDate: Date | null | undefined;
  eventDateFrom: Date | null | undefined;
  eventDateTo: Date | null | undefined;
  fulfillmentStatusId: BrokerNerdsBarkerCoreEnumsFulfillmentStatus | null | undefined;
  paymentStatusId: BrokerNerdsBarkerCoreEnumsPaymentStatus | null | undefined;
  tags: string[];
  antiTags: string[];
} & Omit<GetApiSalesParams, 'fromDate' | 'toDate' | 'eventDateFrom' | 'eventDateTo' | 'fulfillmentStatusId' | 'paymentStatusId'>;

export type ColumnSettingItem = {
  sortIndex?: number;
  key: string;
  name: string;
  visible: boolean; // TODO: Rename this to hide to match the grid eventually.
  width: number | null | undefined;
  pinned: ColumnPinnedType;
};

export enum PricerView {
  Split = 'split',
  Standard = 'standard',
  Bulk = 'bulk',
}

export enum FilterMode {
  Standard = 'standard',
  Locked = 'locked',
}

export type TicketmasterMetadata = {
  TicketmasterId?: string;
  IsResaleEnabled: boolean;
};

export type PurchaseOrder = Omit<BrokerNerdsBarkerCoreModelsInventoryAddListingRequest, 'unitCost' | 'unitPrice' | 'eventId' | 'vendorId' | 'vendorProperties'> & {
  eventId: string | undefined;
  unitCost: number | undefined;
  unitPrice: number | undefined;
  vendorId: string | undefined;
  vendorProperties?: ExtendedVendorProperties;
  key: string;
  oddEven: boolean;
  quantity: number | undefined;
  seatFrom: number | undefined;
};

export type PurchaseOrderGroup = {
  eventIds: string[];
  orders: PurchaseOrder[];
  group: string;
};
